// KanbanDateFilter.js
import React from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody,
    Button,
    ButtonGroup,
    VStack,
    HStack,
    Select,
    Box,
    useToast
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';

const KanbanDateFilter = ({
    appliedDateFilter,
    setAppliedDateFilter,
    dateFilter,
    setDateFilter,
    datePreset,
    setDatePreset,
    focusedInput,
    setFocusedInput
}) => {

    const toast = useToast();

    return (
        <Popover placement="bottom-start" closeOnBlur={true}>
              <PopoverTrigger>
                <Button size="sm" rightIcon={<ChevronDownIcon />}>
                  {appliedDateFilter.startDate && appliedDateFilter.endDate ?
                    `${appliedDateFilter.startDate.format('MMM DD')} - ${appliedDateFilter.endDate.format('MMM DD')}` :
                    "Date Filter"}
                </Button>
              </PopoverTrigger>
              <PopoverContent width="auto" p={3}>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="medium" pb={1}>Filter by Date</PopoverHeader>
                <PopoverBody p={0}>
                  <VStack align="stretch" spacing={2} p={2}>
                    <HStack spacing={2}>
                      <Select
                        width="150px"
                        size="sm"
                        value={datePreset}
                        onChange={(e) => {
                          const preset = e.target.value;
                          setDatePreset(preset);

                          // Set date range based on preset
                          if (preset === 'custom') {
                            // Keep current selection or clear if coming from a preset
                            return;
                          }

                          const now = moment();
                          let startDate, endDate;

                          switch (preset) {
                            case 'today':
                              startDate = moment().startOf('day');
                              endDate = moment().endOf('day');
                              break;
                            case 'yesterday':
                              startDate = moment().subtract(1, 'days').startOf('day');
                              endDate = moment().subtract(1, 'days').endOf('day');
                              break;
                            case 'this_week':
                              startDate = moment().startOf('week');
                              endDate = moment().endOf('week');
                              break;
                            case 'last_week':
                              startDate = moment().subtract(1, 'weeks').startOf('week');
                              endDate = moment().subtract(1, 'weeks').endOf('week');
                              break;
                            case 'this_month':
                              startDate = moment().startOf('month');
                              endDate = moment().endOf('month');
                              break;
                            case 'last_month':
                              startDate = moment().subtract(1, 'months').startOf('month');
                              endDate = moment().subtract(1, 'months').endOf('month');
                              break;
                            default:
                              startDate = null;
                              endDate = null;
                          }

                          setDateFilter({ startDate, endDate });
                        }}
                      >
                        <option value="custom">Custom Range</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="this_week">This Week</option>
                        <option value="last_week">Last Week</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                      </Select>

                      <ButtonGroup size="sm" isAttached variant="outline">
                        <Button
                          colorScheme="blue"
                          onClick={() => {
                            if (dateFilter.startDate && dateFilter.endDate) {
                              // Clear previous filter first
                              setAppliedDateFilter({ startDate: null, endDate: null });

                              // Apply new filter after a short delay to ensure the previous filter is cleared
                              setTimeout(() => {
                                setAppliedDateFilter(dateFilter);
                                toast({
                                  title: "Date filter applied",
                                  description: `Filtering leads from ${dateFilter.startDate.format('MMM DD, YYYY')} to ${dateFilter.endDate.format('MMM DD, YYYY')}`,
                                  status: "info",
                                  duration: 3000,
                                  isClosable: true,
                                });
                              }, 50);
                            } else {
                              toast({
                                title: "Cannot apply filter",
                                description: "Please select both start and end dates",
                                status: "warning",
                                duration: 3000,
                                isClosable: true,
                              });
                            }
                          }}
                          isDisabled={!dateFilter.startDate || !dateFilter.endDate}
                        >
                          Apply
                        </Button>
                        <Button
                          onClick={() => {
                            // First clear the applied filter
                            setAppliedDateFilter({ startDate: null, endDate: null });

                            // Then clear the selection and reset preset
                            setTimeout(() => {
                              setDateFilter({ startDate: null, endDate: null });
                              setDatePreset('custom');

                              // Force a re-render by updating state
                              //setSearchQuery(searchQuery);

                              toast({
                                title: "Date filter cleared",
                                status: "info",
                                duration: 2000,
                                isClosable: true,
                              });
                            }, 50);
                          }}
                          isDisabled={!appliedDateFilter.startDate && !appliedDateFilter.endDate && !dateFilter.startDate && !dateFilter.endDate}
                        >
                          Clear
                        </Button>
                      </ButtonGroup>
                    </HStack>

                    <Box width="300px" zIndex="10">
                      <DateRangePicker
                        startDate={dateFilter.startDate}
                        startDateId="start-date"
                        endDate={dateFilter.endDate}
                        endDateId="end-date"
                        onDatesChange={({ startDate, endDate }) => {
                          setDateFilter({ startDate, endDate });
                          setDatePreset('custom'); // Set to custom when manually selecting dates
                        }}
                        focusedInput={focusedInput}
                        onFocusChange={focusedInput => setFocusedInput(focusedInput)}
                        small={true}
                        showClearDates={true}
                        isOutsideRange={() => false}
                        displayFormat="DD/MM/YYYY"
                      />
                    </Box>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
    );
};

export default KanbanDateFilter;